import type { ServiceCode, ServiceType } from '@common/enums';
import type { IServiceActivity } from '@common/interfaces';
import type {
    GetOrganizationRolesResponseBody,
    GetServiceOrganizationRolesQuery,
    IServicesGetAllItem,
} from '@common/interfaces/api/servicesApi';
import { fetchJson } from '@frontend/store/fetch';
import { getUrlQueryParameters } from './queryParameterUtils';

export interface IService {
    code: ServiceCode;
    id: string;
    name: string;
    serviceType: ServiceType;
    useNewFundingSystem: boolean;
}

const baseUrl = '/api/services';

export async function getAll(
    params: { code?: ServiceCode } | undefined,
    signal: AbortSignal
): Promise<IServicesGetAllItem[]> {
    const url = getUrlQueryParameters<{ code?: ServiceCode }>(baseUrl, params);
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json?.items;
}

export async function getOrganizationRoles(
    serviceId: string,
    query: GetServiceOrganizationRolesQuery | undefined,
    signal: AbortSignal
): Promise<GetOrganizationRolesResponseBody> {
    const url = getUrlQueryParameters<GetServiceOrganizationRolesQuery>(
        `${baseUrl}/${serviceId}/organizationRoles`,
        query
    );

    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function findByCode(code: ServiceCode, signal: AbortSignal): Promise<IServicesGetAllItem> {
    const services = await getAll({ code }, signal);

    if (services.length !== 1 || services[0].code !== code) {
        throw new Error(`Expected to find one service for ${code}`);
    }

    return services[0];
}

export async function getOneService(id: string, signal: AbortSignal): Promise<IService | undefined> {
    const url = `${baseUrl}/${id}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json;
}

export async function getServiceActivities(serviceId: string, signal: AbortSignal): Promise<IServiceActivity[]> {
    const url = `${baseUrl}/${serviceId}/serviceActivities`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json?.items ?? [];
}
